import * as THREE from "https://esm.sh/three@0.185.1";
import { OrbitControls } from "https://esm.sh/three@0.185.1/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "https://esm.sh/three@0.185.1/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "https://esm.sh/three@0.185.1/examples/jsm/loaders/OBJLoader.js";

const MAX_BYTES = 95 * 1024 * 1024;
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 20 * 60 * 1000;
const MAX_CHECKOUT_QUOTES = 10;
const MAX_QUOTE_QUANTITY = 10_000;
const BASKET_STORAGE_KEY = "y3dm-quote-basket-v2";
const LEGACY_BASKET_STORAGE_KEY = "y3dm-quote-basket-v1";
const MATERIAL_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;
const FALLBACK_MATERIALS = [
  {
    id: "pla",
    label: "PLA",
    description: "An easy-to-print, rigid plastic with a clean surface finish and low warping. Best for indoor display pieces, prototypes, and decorative parts that will not see high heat.",
    strength: 2,
    useLabel: "Indoor",
    imageUrl: "",
    colors: [
      catalogColor("Black", "#17191d"),
      catalogColor("Caramel", "#c8792a"),
      catalogColor("Dark Brown", "#51260f"),
      catalogColor("Shiny Dark Red / Gold", "#6f1816", "dual", "#d9a228"),
      catalogColor("Brown", "#8c4e20"),
      catalogColor("Matte Brown", "#754127"),
      catalogColor("White", "#f5f4f0"),
      catalogColor("Silk Gold / Silver", "#d5ae48", "silk", "#b8bcc2"),
      catalogColor("Blue / Green", "#147b90", "dual", "#218d62"),
      catalogColor("Gray", "#88898b"),
      catalogColor("Red / Orange", "#d82b08", "dual", "#ef6a00"),
      catalogColor("Silk Blue / Green", "#00a5c8", "silk", "#29c27f"),
      catalogColor("Blue / Green / Purple Silk", "#168ee8", "silk", "#25d596", "#a13de2"),
      catalogColor("Olive Green", "#747b26"),
      catalogColor("Chocolate Brown", "#572608"),
      catalogColor("Beige", "#e7d6ae"),
      catalogColor("Silk Green", "#20aa51", "silk", "#0b6f3b"),
      catalogColor("Orange / Red", "#ef5a00", "dual", "#d9190b"),
      catalogColor("Red / Blue / Green Silk", "#de2030", "silk", "#176fe8", "#22b86a"),
      catalogColor("Pink", "#d84a91"),
      catalogColor("Red", "#e10b0b"),
      catalogColor("Silk Bronze", "#b37a3c", "silk", "#e1b76f"),
      catalogColor("Gold / Red Silk", "#e4af27", "silk", "#d62718"),
    ],
  },
  {
    id: "pla-wood",
    label: "PLA Wood",
    description: "PLA blended with wood fibers for a warm, natural-looking surface that can be lightly sanded. Best for decorative indoor parts, signs, props, and display pieces.",
    strength: 2,
    useLabel: "Indoor decor",
    imageUrl: "",
    colors: [catalogColor("Wood-Filled", "#a6794d", "wood", "#8a5e38")],
  },
  {
    id: "pla-cf",
    label: "PLA-CF",
    description: "Carbon-fiber-reinforced PLA is stiffer and more dimensionally stable than standard PLA with a clean matte finish. Good for lightweight fixtures, brackets, and structural prototypes used indoors.",
    strength: 3,
    useLabel: "Lightweight",
    imageUrl: "",
    colors: [catalogColor("Black", "#202326")],
  },
  {
    id: "petg",
    label: "PETG",
    description: "A strong, impact-resistant general-purpose plastic with better heat and moisture resistance than PLA. Good for functional parts, brackets, housings, and outdoor items that need toughness.",
    strength: 3,
    useLabel: "Indoor / Outdoor",
    imageUrl: "",
    colors: [
      catalogColor("Blue", "#3ba8c7"),
      catalogColor("Black", "#17191d"),
      catalogColor("White", "#f5f4f0"),
      catalogColor("Gray", "#85898d"),
    ],
  },
  {
    id: "asa-cf",
    label: "ASA-CF",
    description: "Carbon-fiber-reinforced ASA is stiff, dimensionally stable, and weather/UV resistant with a matte technical finish. Good for outdoor fixtures, rigid jigs, and functional components.",
    strength: 4,
    useLabel: "Outdoor",
    imageUrl: "",
    colors: [catalogColor("Black", "#202326")],
  },
  {
    id: "asa",
    label: "ASA",
    description: "ASA is a tough, heat- and UV-resistant plastic made for long-term outdoor use. It is a strong choice for weather-exposed enclosures, automotive parts, fixtures, and functional components.",
    strength: 4,
    useLabel: "Outdoor",
    imageUrl: "",
    colors: [catalogColor("Black", "#17191d"), catalogColor("White", "#f5f4f0")],
  },
  {
    id: "pps-cf",
    label: "PPS-CF",
    description: "A high-temperature, chemically resistant carbon-fiber composite for demanding engineering parts. Best for stiff components exposed to heat, fuels, oils, solvents, or tight dimensional requirements.",
    strength: 5,
    useLabel: "Industrial",
    imageUrl: "",
    colors: [catalogColor("Natural material color", "#222629")],
  },
  {
    id: "abs",
    label: "ABS",
    description: "A tough, impact- and heat-resistant plastic for enclosures, tools, fixtures, and functional parts. ABS works indoors and outdoors; painting or sheltering it helps protect the surface during long-term direct sun exposure.",
    strength: 3,
    useLabel: "Indoor / Outdoor",
    imageUrl: "",
    colors: [
      catalogColor("Black", "#17191d"),
      catalogColor("White", "#f5f4f0"),
      catalogColor("Gray", "#8b8d8f"),
      catalogColor("Burnt Titanium", "#35448e", "silk", "#8f493c"),
    ],
  },
  {
    id: "tpu",
    label: "TPU",
    description: "A flexible, rubber-like material that bends, absorbs impact, and resists abrasion. Good for grips, feet, bumpers, seals, guards, and other parts that need flex.",
    strength: 3,
    useLabel: "Flexible",
    imageUrl: "",
    colors: [
      catalogColor("Blue", "#245fc2"),
      catalogColor("White", "#f5f4f0"),
      catalogColor("Black", "#17191d"),
      catalogColor("Brown", "#8a4d24"),
      catalogColor("Gray", "#85898d"),
    ],
  },
  {
    id: "pa6-gf",
    label: "PA6-GF",
    description: "Glass-fiber-reinforced nylon combines high strength, heat resistance, and dimensional stability for demanding functional parts. It is well suited to tooling, load-bearing brackets, automotive components, and industrial fixtures.",
    strength: 5,
    useLabel: "Industrial",
    imageUrl: "",
    colors: [catalogColor("Natural Gray", "#777a76")],
  },
];

function catalogColor(name, hex, finish = "solid", hex2 = "", hex3 = "") {
  return { name, hex, finish, hex2, hex3, imageUrl: "" };
}

initializeAll(document);
document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));

function initializeAll(scope) {
  if (scope.matches?.("[data-y3dm-quote]") && !scope.dataset.initialized) initialize(scope);
  scope.querySelectorAll("[data-y3dm-quote]").forEach((root) => {
    if (!root.dataset.initialized) initialize(root);
  });
}

function initialize(root) {
  root.dataset.initialized = "true";
  const configElement = document.getElementById(root.dataset.configId);
  const config = JSON.parse(configElement.textContent);
  const elements = {
    fileInput: root.querySelector("[data-file-input]"),
    uploadZone: root.querySelector("[data-upload-zone]"),
    viewer: root.querySelector("[data-viewer]"),
    placeholder: root.querySelector("[data-viewer-placeholder]"),
    modelInfo: root.querySelector("[data-model-info]"),
    fileName: root.querySelector("[data-file-name]"),
    dimensions: root.querySelector("[data-dimensions]"),
    material: root.querySelector("[data-material]"),
    materialOptions: root.querySelector("[data-material-options]"),
    materialInfo: root.querySelector("[data-material-info]"),
    materialName: root.querySelector("[data-material-name]"),
    materialDescription: root.querySelector("[data-material-description]"),
    colorOptions: root.querySelector("[data-color-options]"),
    colorHelp: root.querySelector("[data-color-help]"),
    quantity: root.querySelector("[data-quantity]"),
    calculate: root.querySelector("[data-calculate]"),
    manual: root.querySelector("[data-manual]"),
    status: root.querySelector("[data-status]"),
    result: root.querySelector("[data-result]"),
    quoteNumber: root.querySelector("[data-quote-number]"),
    price: root.querySelector("[data-price]"),
    resultDetail: root.querySelector("[data-result-detail]"),
    quoteAnother: root.querySelector("[data-quote-another]"),
    addCurrent: root.querySelector("[data-add-current]"),
    contactLink: root.querySelector("[data-contact-link]"),
    basket: root.querySelector("[data-basket]"),
    basketItems: root.querySelector("[data-basket-items]"),
    basketCount: root.querySelector("[data-basket-count]"),
    basketSubtotal: root.querySelector("[data-basket-subtotal]"),
    basketShipping: root.querySelector("[data-basket-shipping]"),
    basketTotal: root.querySelector("[data-basket-total]"),
    continentalUs: root.querySelector("[data-continental-us]"),
    basketCheckout: root.querySelector("[data-basket-checkout]"),
    basketLocalNote: root.querySelector("[data-basket-local-note]"),
    largeFileLink: root.querySelector("[data-large-file-link]"),
    turnstile: root.querySelector("[data-turnstile]"),
  };

  const viewer = createViewer(elements.viewer);
  const materials = normalizeMaterials(config.materials, config.colors, config.enabledMaterialIds);
  const materialsById = new Map(materials.map((material) => [material.id, material]));
  const maxQuantity = MAX_QUOTE_QUANTITY;
  const state = {
    file: null,
    object: null,
    selectedColor: null,
    quoteToken: null,
    currentQuote: null,
    basket: loadQuoteBasket(),
    turnstileToken: null,
    turnstileWidgetId: null,
    shippingEstimate: null,
    shippingRequestId: 0,
  };
  const localTurnstileTestToken = ["127.0.0.1", "localhost"].includes(window.location.hostname)
    ? String(config.localTurnstileTestToken || "").trim()
    : "";

  elements.quantity.max = String(maxQuantity);
  elements.quantity.value = "1";

  populateMaterials(elements.material, materials);
  renderMaterialOptions(elements.materialOptions, materials, (material) => {
    if (elements.material.value === material.id) return;
    elements.material.value = material.id;
    renderMaterialSelection();
  });
  configureLargeFileLink(elements.largeFileLink, config);
  renderMaterialSelection();
  renderBasket();
  setupTurnstile().catch((error) => showMessage(error.message, "error"));

  elements.fileInput.addEventListener("change", async () => {
    if (elements.fileInput.files[0]) await selectFile(elements.fileInput.files[0]);
  });
  ["dragenter", "dragover"].forEach((eventName) => {
    elements.uploadZone.addEventListener(eventName, (event) => {
      event.preventDefault();
      elements.uploadZone.classList.add("is-dragging");
    });
  });
  ["dragleave", "drop"].forEach((eventName) => {
    elements.uploadZone.addEventListener(eventName, (event) => {
      event.preventDefault();
      elements.uploadZone.classList.remove("is-dragging");
    });
  });
  elements.uploadZone.addEventListener("drop", async (event) => {
    const file = event.dataTransfer.files[0];
    if (file) await selectFile(file);
  });
  elements.material.addEventListener("change", () => {
    renderMaterialSelection();
  });
  elements.quantity.addEventListener("input", updateButtons);
  elements.calculate.addEventListener("click", () => submitQuote(false));
  elements.manual.addEventListener("click", () => submitQuote(true));
  elements.quoteAnother.addEventListener("click", resetQuoteForm);
  elements.addCurrent.addEventListener("click", () => {
    if (state.currentQuote && addBasketItem(state.currentQuote)) {
      showMessage(`${state.currentQuote.quoteNumber} was added back to your quote basket.`, "success");
    }
  });
  elements.basketCheckout.addEventListener("click", checkoutBasket);
  elements.continentalUs?.addEventListener("change", updateBasketCheckoutButton);

  function renderMaterialSelection() {
    const material = materialsById.get(elements.material.value);
    elements.materialOptions.querySelectorAll("button[data-material-id]").forEach((button) => {
      const selected = button.dataset.materialId === elements.material.value;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", String(selected));
    });
    state.selectedColor = null;
    if (!material) {
      elements.materialInfo.hidden = true;
      elements.materialName.textContent = "";
      elements.materialDescription.textContent = "";
      renderColorOptions(elements.colorOptions, [], null);
      elements.colorHelp.textContent = "Select a material first.";
      updateButtons();
      return;
    }

    elements.materialName.textContent = material.label;
    elements.materialDescription.textContent = material.description;
    elements.materialInfo.hidden = false;
    elements.colorHelp.textContent = material.colors.length
      ? "Choose one of the colors available for this material."
      : "No colors are currently available for this material.";
    renderColorOptions(elements.colorOptions, material.colors, (color, button) => {
      state.selectedColor = color;
      elements.colorOptions.querySelectorAll("button").forEach((candidate) => {
        const selected = candidate === button;
        candidate.classList.toggle("is-selected", selected);
        candidate.setAttribute("aria-pressed", String(selected));
      });
      if (state.object) applyPreviewColor(state.object, color.hex || color.name);
      updateButtons();
    });
    updateButtons();
  }

  async function selectFile(file) {
    try {
      clearMessage();
      validateFile(file);
      state.file = file;
      state.quoteToken = null;
      state.currentQuote = null;
      elements.result.hidden = true;
      elements.fileName.textContent = file.name;
      showMessage("Loading 3D preview…", "working");
      const object = await parseModel(file);
      if (state.object) {
        viewer.scene.remove(state.object);
        disposeObject(state.object);
      }
      state.object = object;
      applyPreviewColor(object, state.selectedColor?.hex || state.selectedColor?.name || "Gray");
      viewer.scene.add(object);
      const dimensions = fitObject(viewer, object);
      elements.dimensions.textContent = `${dimensions.x.toFixed(1)} × ${dimensions.y.toFixed(1)} × ${dimensions.z.toFixed(1)} mm`;
      elements.modelInfo.hidden = false;
      elements.placeholder.hidden = true;
      clearMessage();
      updateButtons();
    } catch (error) {
      state.file = null;
      state.quoteToken = null;
      state.currentQuote = null;
      if (state.object) {
        viewer.scene.remove(state.object);
        disposeObject(state.object);
        state.object = null;
      }
      elements.fileInput.value = "";
      elements.modelInfo.hidden = true;
      elements.placeholder.hidden = false;
      updateButtons();
      showMessage(error.message, "error");
    }
  }

  function updateButtons() {
    const quantity = Number(elements.quantity.value);
    const validQuantity = Number.isInteger(quantity) && quantity >= 1 && quantity <= maxQuantity;
    const ready = Boolean(state.file && elements.material.value && state.selectedColor && validQuantity);
    elements.calculate.disabled = !ready;
    elements.manual.disabled = !ready;
  }

  function selectedQuantity() {
    const quantity = Number(elements.quantity.value);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) {
      throw new Error(`Choose a whole-number copy count from 1 to ${maxQuantity}.`);
    }
    return quantity;
  }

  async function submitQuote(manualRequested) {
    try {
      if (!config.apiBase) throw new Error("The quote API URL has not been configured.");
      if (!state.turnstileToken) throw new Error("Complete the security check, then try again.");
      if (!manualRequested && state.basket.length >= MAX_CHECKOUT_QUOTES) {
        throw new Error(`Your quote basket already has ${MAX_CHECKOUT_QUOTES} parts. Checkout or remove one before quoting another part.`);
      }
      const quantity = selectedQuantity();
      lockControls(true);
      elements.result.hidden = true;
      showMessage(manualRequested ? "Saving your custom quote request…" : "Uploading your model…", "working");
      const created = await apiJson(`${config.apiBase}/api/quotes`, {
        method: "POST",
        body: JSON.stringify({
          filename: state.file.name,
          size: state.file.size,
          material: elements.material.value,
          color: state.selectedColor.name,
          quantity,
          manual_requested: manualRequested,
          turnstile_token: state.turnstileToken,
        }),
      });
      state.quoteToken = created.token;
      const uploadResponse = await fetch(created.upload_url, {
        method: "PUT",
        headers: { "Content-Type": "application/octet-stream" },
        body: state.file,
      });
      if (!uploadResponse.ok) throw new Error(await responseError(uploadResponse));

      elements.quoteNumber.textContent = created.quote_number;
      elements.result.hidden = false;
      if (manualRequested) {
        elements.price.textContent = "Manual review requested";
        elements.resultDetail.textContent = `${quantity} ${quantity === 1 ? "copy" : "copies"}`;
        elements.quoteAnother.hidden = false;
        elements.addCurrent.hidden = true;
        const subject = encodeURIComponent(`Custom 3D print request ${created.quote_number}`);
        const body = encodeURIComponent(`Please review quote ${created.quote_number} for ${quantity} ${quantity === 1 ? "copy" : "copies"}.\n\nMy special instructions are:\n`);
        elements.contactLink.href = `mailto:${String(config.contactEmail).trim()}?subject=${subject}&body=${body}`;
        elements.contactLink.hidden = false;
        showMessage(`Your file was saved as ${created.quote_number}. Contact us and include this number.`, "success");
        resetTurnstile();
        lockControls(false);
        return;
      }

      elements.price.textContent = "Calculating…";
      elements.resultDetail.textContent = `${quantity} ${quantity === 1 ? "copy" : "copies"}`;
      elements.quoteAnother.hidden = true;
      elements.addCurrent.hidden = true;
      elements.contactLink.hidden = true;
      showMessage(`Calculating ${created.quote_number}. This can take a few minutes.`, "working");
      await pollQuote(created.token);
    } catch (error) {
      showMessage(error.message, "error");
      resetTurnstile();
      lockControls(false);
    }
  }

  async function pollQuote(token) {
    const started = Date.now();
    while (Date.now() - started < POLL_TIMEOUT_MS) {
      const quote = await apiJson(`${config.apiBase}/api/quotes/${token}`);
      if (quote.status === "ready") {
        const quantity = Number(quote.quantity || selectedQuantity());
        const currency = String(quote.currency || "USD").toUpperCase();
        elements.price.textContent = formatCurrency(quote.price, currency);
        elements.resultDetail.textContent = `${quantity} ${quantity === 1 ? "copy" : "copies"}`;
        const material = materialsById.get(quote.material || elements.material.value);
        state.currentQuote = {
          token,
          quoteNumber: quote.quote_number,
          filename: state.file?.name || "3D model",
          material: quote.material || elements.material.value,
          materialLabel: material?.label || quote.material || elements.material.value,
          color: quote.color || state.selectedColor?.name || "Selected color",
          quantity,
          price: Number(quote.price),
          currency,
        };
        const added = addBasketItem(state.currentQuote);
        const checkoutEnabled = config.checkoutEnabled !== false;
        elements.quoteAnother.hidden = false;
        elements.addCurrent.hidden = added;
        showMessage(
          checkoutEnabled
            ? added
              ? "Your quote is ready and was added to the basket. Quote another part or checkout when finished."
              : "Your quote is ready, but the basket is full. Remove a part before adding this quote."
            : "Local test passed: the model was sliced, priced, and added to the test basket.",
          "success",
        );
        resetTurnstile();
        lockControls(false);
        return;
      }
      if (quote.status === "failed") {
        throw new Error(quote.message || `Automatic quoting failed. Contact us with ${quote.quote_number}.`);
      }
      await delay(POLL_INTERVAL_MS);
    }
    throw new Error("The quote is taking longer than expected. Save the quote number and contact us.");
  }

  async function checkoutBasket() {
    try {
      if (config.checkoutEnabled === false) {
        showMessage("Shopify checkout is intentionally disabled in local test mode.", "success");
        return;
      }
      if (!state.basket.length) throw new Error("Add at least one completed quote before checkout.");
      if (!state.shippingEstimate) throw new Error("Wait for the shipping estimate before checkout.");
      if (elements.continentalUs && !elements.continentalUs.checked) {
        throw new Error("Confirm that the shipping address is in the continental United States.");
      }
      elements.basketCheckout.disabled = true;
      showMessage("Creating one secure Shopify checkout for all quoted parts…", "working");
      const result = await apiJson(`${config.apiBase}/api/checkouts`, {
        method: "POST",
        body: JSON.stringify({
          tokens: state.basket.map((item) => item.token),
          continental_us_confirmed: true,
        }),
      });
      state.basket = [];
      saveQuoteBasket(state.basket);
      renderBasket();
      window.location.assign(result.checkout_url);
    } catch (error) {
      elements.basketCheckout.disabled = false;
      showMessage(error.message, "error");
    }
  }

  function addBasketItem(item) {
    const existingIndex = state.basket.findIndex((candidate) => candidate.token === item.token);
    if (existingIndex >= 0) {
      state.basket[existingIndex] = item;
    } else {
      if (state.basket.length >= MAX_CHECKOUT_QUOTES) return false;
      state.basket.push(item);
    }
    saveQuoteBasket(state.basket);
    renderBasket();
    return true;
  }

  async function removeBasketItem(token, button) {
    const item = state.basket.find((candidate) => candidate.token === token);
    if (!item) return;
    const confirmed = window.confirm(
      `Remove ${item.quoteNumber}? Its uploaded model, sliced file, and quote record will be permanently deleted after 7 days.`,
    );
    if (!confirmed) return;
    try {
      button.disabled = true;
      button.textContent = "Removing…";
      await apiJson(`${config.apiBase}/api/quotes/${token}`, { method: "DELETE" });
      state.basket = state.basket.filter((candidate) => candidate.token !== token);
      saveQuoteBasket(state.basket);
      if (state.currentQuote?.token === token) state.currentQuote = null;
      renderBasket();
      showMessage(
        `${item.quoteNumber} was removed. Its cloud and laptop files are scheduled for permanent deletion after 7 days.`,
        "success",
      );
    } catch (error) {
      button.disabled = false;
      button.textContent = "Remove";
      showMessage(error.message, "error");
    }
  }

  function renderBasket() {
    elements.basket.hidden = state.basket.length === 0;
    elements.basketItems.replaceChildren();
    elements.basketCount.textContent = String(state.basket.length);
    elements.basketCheckout.hidden = config.checkoutEnabled === false;
    elements.basketLocalNote.hidden = config.checkoutEnabled !== false;
    state.shippingEstimate = null;
    state.shippingRequestId += 1;
    if (!state.basket.length) {
      if (elements.continentalUs) elements.continentalUs.checked = false;
      updateBasketCheckoutButton();
      return;
    }

    state.basket.forEach((item) => {
      const row = document.createElement("div");
      row.className = "y3dm-basket-item";

      const details = document.createElement("div");
      details.className = "y3dm-basket-item__details";
      const title = document.createElement("strong");
      title.textContent = item.quoteNumber;
      const filename = document.createElement("span");
      filename.textContent = item.filename;
      const options = document.createElement("span");
      options.textContent = `${item.materialLabel} / ${item.color} / ${item.quantity} ${item.quantity === 1 ? "copy" : "copies"}`;
      details.append(title, filename, options);

      const actions = document.createElement("div");
      actions.className = "y3dm-basket-item__actions";
      const price = document.createElement("strong");
      price.textContent = formatCurrency(item.price, item.currency);
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "y3dm-basket-item__remove";
      remove.textContent = "Remove";
      remove.setAttribute("aria-label", `Remove ${item.quoteNumber} from checkout`);
      remove.addEventListener("click", () => removeBasketItem(item.token, remove));
      actions.append(price, remove);
      row.append(details, actions);
      elements.basketItems.appendChild(row);
    });

    const currency = state.basket[0].currency || "USD";
    const subtotal = state.basket.reduce((sum, item) => sum + Number(item.price), 0);
    elements.basketSubtotal.textContent = formatCurrency(subtotal, currency);
    if (elements.basketShipping) elements.basketShipping.textContent = "Calculating…";
    if (elements.basketTotal) elements.basketTotal.textContent = "Calculating…";
    updateBasketCheckoutButton();
    refreshShippingEstimate();
    if (state.currentQuote) {
      elements.addCurrent.hidden = state.basket.some((item) => item.token === state.currentQuote.token);
    }
  }

  async function refreshShippingEstimate() {
    if (!state.basket.length || !config.apiBase) return;
    const requestId = ++state.shippingRequestId;
    try {
      const estimate = await apiJson(`${config.apiBase}/api/checkouts/estimate`, {
        method: "POST",
        body: JSON.stringify({ tokens: state.basket.map((item) => item.token) }),
      });
      if (requestId !== state.shippingRequestId) return;
      state.shippingEstimate = estimate;
      const currency = String(estimate.currency || state.basket[0]?.currency || "USD").toUpperCase();
      if (elements.basketShipping) {
        elements.basketShipping.textContent = formatCurrency(estimate.shipping_price, currency);
      }
      if (elements.basketTotal) {
        elements.basketTotal.textContent = formatCurrency(estimate.estimated_total_before_tax, currency);
      }
      updateBasketCheckoutButton();
    } catch (error) {
      if (requestId !== state.shippingRequestId) return;
      state.shippingEstimate = null;
      if (elements.basketShipping) elements.basketShipping.textContent = "Unavailable";
      if (elements.basketTotal) elements.basketTotal.textContent = "Unavailable";
      updateBasketCheckoutButton();
      showMessage(`Shipping estimate: ${error.message}`, "error");
    }
  }

  function updateBasketCheckoutButton() {
    if (!elements.basketCheckout) return;
    if (config.checkoutEnabled === false) {
      elements.basketCheckout.disabled = true;
      return;
    }
    const confirmed = !elements.continentalUs || elements.continentalUs.checked;
    elements.basketCheckout.disabled = !state.basket.length || !state.shippingEstimate || !confirmed;
  }

  function resetQuoteForm() {
    state.file = null;
    state.quoteToken = null;
    state.currentQuote = null;
    elements.fileInput.value = "";
    elements.quantity.value = "1";
    elements.result.hidden = true;
    elements.contactLink.hidden = true;
    elements.quoteAnother.hidden = true;
    elements.addCurrent.hidden = true;
    elements.modelInfo.hidden = true;
    elements.placeholder.hidden = false;
    if (state.object) {
      viewer.scene.remove(state.object);
      disposeObject(state.object);
      state.object = null;
    }
    clearMessage();
    updateButtons();
    elements.uploadZone.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function lockControls(locked) {
    elements.fileInput.disabled = locked;
    elements.material.disabled = locked;
    elements.quantity.disabled = locked;
    elements.materialOptions.querySelectorAll("button").forEach((button) => {
      button.disabled = locked;
    });
    elements.colorOptions.querySelectorAll("button").forEach((button) => {
      button.disabled = locked;
    });
    if (locked) {
      elements.calculate.disabled = true;
      elements.manual.disabled = true;
    } else {
      updateButtons();
    }
  }

  function showMessage(message, kind) {
    elements.status.hidden = false;
    elements.status.dataset.kind = kind;
    elements.status.textContent = message;
  }

  function clearMessage() {
    elements.status.hidden = true;
    elements.status.textContent = "";
    delete elements.status.dataset.kind;
  }

  function resetTurnstile() {
    if (localTurnstileTestToken) {
      state.turnstileToken = localTurnstileTestToken;
      return;
    }
    state.turnstileToken = null;
    if (state.turnstileWidgetId !== null && window.turnstile?.reset) {
      try {
        window.turnstile.reset(state.turnstileWidgetId);
      } catch {
        state.turnstileWidgetId = null;
      }
    }
  }

  async function setupTurnstile() {
    if (localTurnstileTestToken) {
      state.turnstileToken = localTurnstileTestToken;
      if (elements.turnstile) {
        elements.turnstile.classList.add("y3dm-turnstile--local-ready");
        elements.turnstile.textContent = "Local test security check: ready";
      }
      return;
    }
    if (!elements.turnstile || !config.turnstileSiteKey) return;
    const api = await waitForTurnstile();
    state.turnstileWidgetId = api.render(elements.turnstile, {
      sitekey: config.turnstileSiteKey,
      action: "y3dm_quote",
      theme: "auto",
      callback: (token) => {
        state.turnstileToken = token;
      },
      "expired-callback": () => {
        state.turnstileToken = null;
      },
      "error-callback": () => {
        state.turnstileToken = null;
        showMessage("The security check could not load. Refresh it and try again.", "error");
      },
    });
  }
}

function waitForTurnstile(timeoutMilliseconds = 15000) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const check = () => {
      if (window.turnstile?.render) {
        resolve(window.turnstile);
        return;
      }
      if (Date.now() - started >= timeoutMilliseconds) {
        reject(new Error("The security check did not load. Refresh the page and try again."));
        return;
      }
      window.setTimeout(check, 100);
    };
    check();
  });
}

function validateFile(file) {
  const dot = file.name.lastIndexOf(".");
  const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
  if (![".stl", ".obj"].includes(extension)) throw new Error("Please select an STL or OBJ file.");
  if (!file.size || file.size > MAX_BYTES) throw new Error("The model must be 95 MB or smaller.");
}

async function parseModel(file) {
  const dot = file.name.lastIndexOf(".");
  const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
  if (extension === ".stl") {
    const geometry = new STLLoader().parse(await file.arrayBuffer());
    geometry.computeVertexNormals();
    return new THREE.Mesh(geometry, new THREE.MeshStandardMaterial());
  }
  const object = new OBJLoader().parse(await file.text());
  let meshCount = 0;
  object.traverse((child) => {
    if (child.isMesh) meshCount += 1;
  });
  if (!meshCount) throw new Error("This OBJ contains no previewable mesh.");
  return object;
}

function createViewer(container) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf4f7fb);
  const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100000);
  camera.position.set(80, 70, 80);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.appendChild(renderer.domElement);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;

  scene.add(new THREE.HemisphereLight(0xffffff, 0x54657a, 2.2));
  const key = new THREE.DirectionalLight(0xffffff, 2.4);
  key.position.set(100, 140, 90);
  scene.add(key);

  const resize = () => {
    const width = Math.max(container.clientWidth, 280);
    const height = Math.max(container.clientHeight, 360);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  };
  new ResizeObserver(resize).observe(container);
  resize();

  const animate = () => {
    controls.update();
    renderer.render(scene, camera);
    requestAnimationFrame(animate);
  };
  animate();
  return { scene, camera, controls };
}

function fitObject(viewer, object) {
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) throw new Error("The model has no measurable geometry.");
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  object.position.sub(center);
  const maxDimension = Math.max(size.x, size.y, size.z);
  const distance = maxDimension / (2 * Math.tan(THREE.MathUtils.degToRad(viewer.camera.fov / 2))) * 1.65;
  viewer.camera.position.set(distance, distance * 0.75, distance);
  viewer.camera.near = Math.max(maxDimension / 1000, 0.01);
  viewer.camera.far = Math.max(maxDimension * 100, 1000);
  viewer.camera.updateProjectionMatrix();
  viewer.controls.target.set(0, 0, 0);
  viewer.controls.update();
  return size;
}

function applyPreviewColor(object, colorName) {
  const color = new THREE.Color(colorForName(colorName));
  object.traverse((child) => {
    if (child.isMesh) {
      if (Array.isArray(child.material)) child.material.forEach((material) => material.dispose());
      else if (child.material) child.material.dispose();
      child.material = new THREE.MeshStandardMaterial({ color, roughness: 0.62, metalness: 0.04 });
    }
  });
}

function disposeObject(object) {
  object.traverse((child) => {
    if (!child.isMesh) return;
    if (child.geometry) child.geometry.dispose();
    if (Array.isArray(child.material)) child.material.forEach((material) => material.dispose());
    else if (child.material) child.material.dispose();
  });
}

function colorForName(name) {
  const supplied = String(name || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(supplied)) return supplied;
  const palette = {
    black: "#17191d", white: "#f5f5f2", gray: "#80858d", grey: "#80858d",
    red: "#c93434", blue: "#2563b8", green: "#2f8b57", yellow: "#e4b72f",
    orange: "#e97827", purple: "#7651a8", pink: "#e378a4", natural: "#d9d0ba",
    clear: "#dcecf0", silver: "#a8acb2", gold: "#c8a84d", brown: "#76503b",
  };
  return palette[supplied.toLowerCase() || "gray"] || "#5b8fd9";
}

function normalizeMaterials(materials, legacyColors = {}, enabledMaterialIds = "") {
  const configured = Array.isArray(materials) ? materials : [];
  const configuredById = new Map();
  configured.forEach((material) => {
    const id = String(material?.id || "").trim().toLowerCase();
    if (MATERIAL_ID_PATTERN.test(id)) configuredById.set(id, material);
  });
  const orderedIds = [
    ...FALLBACK_MATERIALS.map((material) => material.id),
    ...[...configuredById.keys()].filter((id) => !FALLBACK_MATERIALS.some((material) => material.id === id)),
  ];
  const enabledValues = Array.isArray(enabledMaterialIds)
    ? enabledMaterialIds
    : String(enabledMaterialIds || "").split(",");
  const enabled = new Set(enabledValues.map((value) => String(value).trim().toLowerCase()).filter(Boolean));
  const filterEnabled = enabled.size > 0;
  const legacyAbsDescription = "A durable, impact-resistant plastic with good heat resistance and machinability. Good for enclosures, prototypes, tools, fixtures, and functional parts used indoors.";

  return orderedIds.flatMap((id) => {
    if (filterEnabled && !enabled.has(id)) return [];
    const base = FALLBACK_MATERIALS.find((material) => material.id === id) || {};
    const custom = configuredById.get(id) || {};
    const colorMap = new Map();
    const addColors = (rawColors) => {
      const values = Array.isArray(rawColors)
        ? rawColors
        : String(rawColors || "").split(",").map((name) => ({ name }));
      values.forEach((color) => {
        const entry = typeof color === "string" ? { name: color } : color;
        const name = String(entry?.name || "").trim().slice(0, 80);
        if (!name) return;
        const suppliedHex = String(entry?.hex || "").trim();
        const suppliedHex2 = String(entry?.hex2 || "").trim();
        const suppliedHex3 = String(entry?.hex3 || "").trim();
        const finish = ["solid", "dual", "silk", "wood"].includes(String(entry?.finish || "").toLowerCase())
          ? String(entry.finish).toLowerCase()
          : "solid";
        colorMap.set(name.toLowerCase(), {
          name,
          hex: /^#[0-9a-f]{6}$/i.test(suppliedHex) ? suppliedHex : colorForName(name),
          hex2: /^#[0-9a-f]{6}$/i.test(suppliedHex2) ? suppliedHex2 : "",
          hex3: /^#[0-9a-f]{6}$/i.test(suppliedHex3) ? suppliedHex3 : "",
          finish,
          imageUrl: String(entry?.imageUrl || "").trim(),
        });
      });
    };
    addColors(base.colors || []);
    if (legacyColors && Object.prototype.hasOwnProperty.call(legacyColors, id)) addColors(legacyColors[id]);
    addColors(custom.colors || []);
    const hiddenColors = new Set(
      String(custom.hiddenColors || "")
        .split(",")
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean),
    );

    let description = String(custom.description || base.description || "").trim().slice(0, 600);
    if (id === "abs" && description === legacyAbsDescription) description = base.description;
    let useLabel = String(custom.useLabel || base.useLabel || "General use").trim().slice(0, 40);
    if (id === "abs" && useLabel === "Indoor") useLabel = "Indoor / Outdoor";
    return [{
      id,
      label: String(custom.label || base.label || id).trim().slice(0, 80),
      description,
      strength: Math.min(5, Math.max(1, Number.parseInt(custom.strength ?? base.strength, 10) || 3)),
      useLabel,
      imageUrl: String(custom.imageUrl || base.imageUrl || "").trim(),
      colors: [...colorMap.values()].filter((color) => !hiddenColors.has(color.name.toLowerCase())),
    }];
  });
}

function populateMaterials(select, materials) {
  select.replaceChildren(new Option(materials.length ? "Select a material" : "No materials configured", ""));
  materials.forEach((material) => select.add(new Option(material.label, material.id)));
  select.disabled = materials.length === 0;
}

function renderMaterialOptions(container, materials, onSelect) {
  container.replaceChildren();
  materials.forEach((material) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "y3dm-material-card";
    button.dataset.materialId = material.id;
    button.setAttribute("aria-pressed", "false");
    button.setAttribute("aria-label", `Select ${material.label}, strength ${material.strength} out of 5, ${material.useLabel}`);

    const circle = document.createElement("span");
    circle.className = "y3dm-material-card__circle";
    if (material.imageUrl) {
      const image = document.createElement("img");
      image.src = material.imageUrl;
      image.alt = `${material.label} material`;
      image.loading = "lazy";
      circle.appendChild(image);
    } else {
      const fallback = document.createElement("span");
      fallback.className = "y3dm-material-card__fallback";
      fallback.textContent = material.label.slice(0, 8);
      fallback.setAttribute("aria-hidden", "true");
      circle.appendChild(fallback);
    }
    button.appendChild(circle);

    const name = document.createElement("span");
    name.className = "y3dm-material-card__name";
    name.textContent = material.label;
    button.appendChild(name);

    const strength = document.createElement("span");
    strength.className = "y3dm-material-card__strength";
    strength.textContent = `Strength ${material.strength}/5`;
    button.appendChild(strength);

    const use = document.createElement("span");
    use.className = "y3dm-material-card__use";
    use.textContent = material.useLabel;
    button.appendChild(use);

    if (onSelect) button.addEventListener("click", () => onSelect(material, button));
    container.appendChild(button);
  });
}

function configureLargeFileLink(link, config) {
  if (!link) return;
  const configuredUrl = String(config.largeFileHelpUrl || "").trim();
  if (configuredUrl) {
    link.href = configuredUrl;
    return;
  }
  const contactEmail = String(config.contactEmail || "").trim();
  if (!contactEmail) {
    link.hidden = true;
    return;
  }
  const subject = encodeURIComponent("3D model larger than 95 MB");
  const body = encodeURIComponent("I need help sending a 3D model larger than 95 MB.\n\nFile name:\nApproximate size:\n");
  link.href = `mailto:${contactEmail}?subject=${subject}&body=${body}`;
}

function renderColorOptions(container, colors, onSelect) {
  container.replaceChildren();
  colors.forEach((color) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "y3dm-color-card";
    button.setAttribute("aria-pressed", "false");
    button.setAttribute("aria-label", `Select ${color.name}`);

    if (color.imageUrl) {
      const image = document.createElement("img");
      image.src = color.imageUrl;
      image.alt = `${color.name} filament color`;
      image.loading = "lazy";
      button.appendChild(image);
    } else {
      const swatch = document.createElement("span");
      swatch.className = "y3dm-color-card__swatch";
      swatch.style.background = swatchBackground(color);
      swatch.setAttribute("aria-hidden", "true");
      button.appendChild(swatch);
    }

    const label = document.createElement("span");
    label.className = "y3dm-color-card__label";
    label.textContent = color.name;
    button.appendChild(label);
    if (onSelect) button.addEventListener("click", () => onSelect(color, button));
    container.appendChild(button);
  });
}

function swatchBackground(color) {
  const primary = color.hex || "#808080";
  const secondary = color.hex2 || primary;
  const tertiary = color.hex3 || secondary;
  if (color.finish === "wood") {
    return `repeating-linear-gradient(15deg, ${primary} 0 9px, ${secondary} 9px 12px, ${primary} 12px 18px)`;
  }
  if (color.finish === "silk") {
    return `linear-gradient(135deg, ${primary} 0%, ${secondary} 48%, ${tertiary} 100%)`;
  }
  if (color.finish === "dual") {
    return `linear-gradient(135deg, ${primary} 0 49%, ${secondary} 51% 100%)`;
  }
  return primary;
}

async function apiJson(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!response.ok) throw new Error(await responseError(response));
  return response.json();
}

async function responseError(response) {
  try {
    const body = await response.json();
    return body.error || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

function delay(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function formatCurrency(value, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(Number(value));
}

function loadQuoteBasket() {
  try {
    let raw = localStorage.getItem(BASKET_STORAGE_KEY);
    let parsed;
    if (raw) {
      const envelope = JSON.parse(raw);
      parsed = Array.isArray(envelope) ? envelope : envelope?.items;
    } else {
      raw = sessionStorage.getItem(LEGACY_BASKET_STORAGE_KEY)
        || localStorage.getItem(LEGACY_BASKET_STORAGE_KEY);
      parsed = JSON.parse(raw || "[]");
    }
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    const items = parsed.flatMap((item) => {
      const token = String(item?.token || "").toLowerCase();
      const price = Number(item?.price);
      const quantity = Number(item?.quantity);
      if (!/^[a-f0-9]{32}$/.test(token) || seen.has(token) || !Number.isFinite(price) || price < 0) return [];
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUOTE_QUANTITY) return [];
      seen.add(token);
      return [{
        token,
        quoteNumber: String(item.quoteNumber || "Quote").slice(0, 40),
        filename: String(item.filename || "3D model").slice(0, 180),
        material: String(item.material || "").slice(0, 40),
        materialLabel: String(item.materialLabel || item.material || "Material").slice(0, 80),
        color: String(item.color || "Selected color").slice(0, 80),
        quantity,
        price,
        currency: /^[A-Z]{3}$/.test(String(item.currency || "USD").toUpperCase())
          ? String(item.currency || "USD").toUpperCase()
          : "USD",
      }];
    }).slice(0, MAX_CHECKOUT_QUOTES);
    if (items.length) saveQuoteBasket(items);
    return items;
  } catch {
    return [];
  }
}

function saveQuoteBasket(items) {
  try {
    localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify({ version: 2, items }));
    sessionStorage.removeItem(LEGACY_BASKET_STORAGE_KEY);
    localStorage.removeItem(LEGACY_BASKET_STORAGE_KEY);
  } catch {
    // The quote still works if the browser blocks persistent storage.
  }
}
